import apiV2 from '../api/v2';

// Access section (the IAM console): who holds what, and what roles and
// permissions mean. Types below mirror backend/src/iam/*.ts field-for-field.

export type IamScopeType = 'platform' | 'practice' | 'patient';

export interface IamScope {
    readonly type: IamScopeType;
    /** Null only when type is 'platform', which has no identifier. */
    readonly id: string | null;
}

/** 'platform' or 'practice:12' - mirrors backend/src/iam/scope.ts formatScope. */
export function formatIamScope(scope: IamScope): string {
    return scope.id == null ? scope.type : `${scope.type}:${scope.id}`;
}

export type IamGrantSource = 'membership' | 'assignment' | 'manual' | 'impersonation';

// Mirrors backend/src/iam/permissions/*.ts. A typo'd permission fails closed at
// runtime (hasPermission returns false); this union turns it into a compile error.
export type KnownPermission =
    | 'billing.get'
    | 'billing.update'
    | 'billing.delete'
    | 'products.get'
    | 'products.update'
    | 'products.delete'
    | 'practices.get'
    | 'practices.update'
    | 'practices.delete'
    | 'practices.members.get'
    | 'practices.members.update'
    | 'practices.members.delete'
    | 'practices.provisioning.update'
    | 'practices.itero.get'
    | 'practices.itero.update'
    | 'users.get'
    | 'users.update'
    | 'users.delete'
    | 'iam.get'
    | 'iam.update'
    | 'events.get'
    | 'events.update'
    | 'events.delete'
    | 'resources.get'
    | 'resources.update'
    | 'resources.delete'
    | 'apps.administrator.access'
    | 'apps.dashboard.access'
    | 'apps.orthodontist.access';

export interface CatalogPermission {
    readonly id: string;
    readonly group: string;
    readonly routeCount: number;
}

export interface CatalogRoleFlattenedPermission {
    readonly permission: string;
    readonly chains: readonly (readonly string[])[];
}

interface CatalogRoleShared {
    readonly key: string;
    readonly desc: string;
    readonly includes: readonly string[];
    readonly own: readonly string[];
    readonly depth: number;
    readonly grantable: boolean;
    readonly flattened: readonly CatalogRoleFlattenedPermission[];
}

/** A grantable role - the only kind with a human-facing `title`; the dotted `key` is for debugging. */
export interface CatalogRoleEntry extends CatalogRoleShared {
    readonly kind: 'role';
    readonly title: string;
}

/** Composition-only, never grantable directly - no `title`, the dotted `key` IS its display name. */
export interface CatalogBundleEntry extends CatalogRoleShared {
    readonly kind: 'bundle';
}

export type CatalogRole = CatalogRoleEntry | CatalogBundleEntry;

export type RouteStatus = 'migrated' | 'legacy' | 'public' | 'open';

export interface RouteInfo {
    readonly method: string;
    readonly path: string;
    readonly area: string;
    readonly permission: string | null;
    readonly publicReason: string | null;
    readonly legacyGuards: readonly string[];
    readonly status: RouteStatus;
}

export interface CatalogSummary {
    readonly routeCount: number;
    readonly byStatus: Readonly<Record<RouteStatus, number>>;
    readonly permissionCount: number;
    readonly unwiredPermissions: readonly string[];
}

export interface IamCatalog {
    readonly permissions: readonly CatalogPermission[];
    readonly roles: readonly CatalogRole[];
    readonly routes: readonly RouteInfo[];
    readonly summary: CatalogSummary;
}

/** GET /iam/me - permissions from the caller's stored grants only (not derived isAdmin roles). */
export async function fetchMyIamPermissions(): Promise<readonly string[]> {
    const response = await apiV2.get<{ success: boolean; data: { permissions: string[] } }>(
        '/iam/me'
    );
    return response.data.data.permissions;
}

export async function fetchIamCatalog(): Promise<IamCatalog> {
    const response = await apiV2.get<{ success: boolean; data: IamCatalog }>('/iam/catalog');
    return response.data.data;
}

// ---------------------------------------------------------------------------
// Users: who holds what.

export interface IamGrant {
    readonly roleKey: string;
    readonly roleKind: 'role' | 'bundle';
    readonly scope: IamScope;
    /** Human name for the scope target, e.g. the practice's name. Null for platform. */
    readonly scopeLabel: string | null;
    readonly source: IamGrantSource;
    /** Null for automation-originated grants (source 'membership' / 'assignment'). */
    readonly grantedBy: string | null;
    readonly grantedAt: string | null;
    readonly expiresAt: string | null;
    readonly revokedAt: string | null;
    /** The stored grant's row id, addressed by `DELETE /iam/grants/:grantId`. */
    readonly id?: string;
}

export interface IamGrantSummary {
    readonly roleKey: string;
    readonly roleKind: 'role' | 'bundle';
    readonly scope: IamScope;
    readonly scopeLabel: string | null;
    readonly source: IamGrantSource;
}

export interface IamUserListItem {
    readonly id: string;
    readonly email: string;
    readonly name: string;
    readonly grants: readonly IamGrantSummary[];
    readonly effectiveCount: number;
    readonly unwiredCount: number;
    readonly lastActive: string | null;
}

export interface IamUsersListMeta {
    readonly total: number;
    readonly page: number;
    readonly limit: number;
    readonly totalPages: number;
}

export interface IamUsersListParams {
    page: number;
    limit: number;
    q?: string;
    role?: string;
    sort?: 'name' | 'activity';
    order?: 'asc' | 'desc';
}

export async function fetchIamUsers(
    params: IamUsersListParams
): Promise<{ data: IamUserListItem[]; meta: IamUsersListMeta }> {
    const query: Record<string, string> = {
        page: String(params.page),
        limit: String(params.limit)
    };
    if (params.q) query.q = params.q;
    if (params.role) query.role = params.role;
    if (params.sort) query.sort = params.sort;
    if (params.order) query.order = params.order;

    const response = await apiV2.get<{
        success: boolean;
        data: IamUserListItem[];
        meta: IamUsersListMeta;
    }>('/iam/users', { params: query });
    return { data: response.data.data, meta: response.data.meta };
}

/** `grantIndex` is a positional index into `IamUserDetail.grants`, not an id. */
export interface IamEffectivePermissionEntry {
    readonly grantIndex: number;
    readonly roleKey: string;
    readonly chain: readonly string[];
}

/** A permission the user holds at one scope; `entries` is sorted shortest chain first. */
export interface IamEffectivePermission {
    readonly permission: string;
    readonly group: string;
    readonly scope: IamScope;
    readonly scopeLabel: string | null;
    readonly wired: boolean;
    readonly entries: readonly IamEffectivePermissionEntry[];
}

export interface IamUserDetailUser {
    readonly id: string;
    readonly email: string;
    readonly name: string;
    readonly isAdmin: boolean;
    readonly practices: readonly string[];
}

export interface IamUserDetail {
    readonly user: IamUserDetailUser;
    readonly grants: readonly IamGrant[];
    readonly effective: readonly IamEffectivePermission[];
}

export async function fetchIamUser(userId: string): Promise<IamUserDetail> {
    const response = await apiV2.get<{ success: boolean; data: IamUserDetail }>(
        `/iam/users/${encodeURIComponent(userId)}`
    );
    return response.data.data;
}

// ---------------------------------------------------------------------------
// Grants: creating and revoking manual grants (the user drawer's Add grant form
// and per-grant revoke action). Mirrors backend/src/routes/api/v2/iam/grants.*.

export interface GrantableRole {
    readonly key: string;
    readonly title: string;
    readonly desc: string;
    /** True once any route enforces a permission the role confers; not a filter on the picker. */
    readonly grantable: boolean;
}

export async function fetchGrantableRoles(): Promise<GrantableRole[]> {
    const response = await apiV2.get<{ success: boolean; data: GrantableRole[] }>('/iam/grantable');
    return response.data.data;
}

export interface CreateGrantBody {
    readonly userId: string;
    readonly roleKey: string;
    readonly scopeType: IamScopeType;
    /** Required unless `scopeType` is 'platform'; server's `validateScope` is the real guarantee. */
    readonly scopeId?: string;
    readonly expiresAt?: string;
}

/** Raw stored shape from POST/DELETE /iam/grants; `IamGrant` is the read-side display view. */
export interface IamGrantRecord {
    readonly id: string;
    readonly userId: string;
    readonly roleKey: string;
    readonly scopeType: IamScopeType;
    readonly scopeId: string | null;
    readonly source: IamGrantSource;
    readonly grantedBy: string | null;
    readonly grantedAt: string;
    readonly expiresAt: string | null;
    readonly revokedAt: string | null;
}

export async function createGrant(body: CreateGrantBody): Promise<IamGrantRecord> {
    const response = await apiV2.post<{ success: boolean; data: IamGrantRecord }>(
        '/iam/grants',
        body
    );
    return response.data.data;
}

/** 204 on success - the row stays (soft revoke), so there is no body to unwrap. */
export async function revokeGrant(grantId: string): Promise<void> {
    await apiV2.delete(`/iam/grants/${encodeURIComponent(grantId)}`);
}
